import {
    Button,
    Col,
    Form,
    Grid,
    Input,
    List,
    message,
    Radio,
    Row,
    Select,
    Space,
} from 'antd';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import axios, { CancelToken } from 'axios';
import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import Loading from '../../../components/Loading';
import { topTitleContext } from '../../../contexts/TopTitle';
import { errorsListing } from '../../../helpers/errorListing';
import { useAxios } from '../../../hooks/axios/useAxios';
import { MethodList } from '../../../interfaces/Method';
import { EnRoles, listUsersRoles } from '../../../interfaces/Roles';
import { Store } from '../../Financial/types';
import { ESelection } from '../models';

const CreateAdmin: React.FC = () => {
    const [form] = Form.useForm();
    const breakpoints = Grid.useBreakpoint();
    const [storeClientsSelected, setStoreClientsSelected] =
        React.useState<ESelection>(ESelection.ALL_STORES);
    const [isEditMode, setIsEditMode] = React.useState(true);
    const [stores, setStores] = React.useState<Store[]>([]);
    const [loadedStores, setLoadedStores] = React.useState<Store[]>([]);
    const [isChecked, setIsChecked] = React.useState<string[]>([]);
    const [isCheckAll, setIsCheckAll] = React.useState(false);
    const { response, error, loading, fetch } = useAxios();

    const { title, titleValue } = useContext(topTitleContext);
    const titlePage = 'Cadastrar Usuário';
    useEffect(() => {
        titleValue(titlePage);
    }, [title]);
    const {
        loading: loadingGet,
        fetch: fetchGet,
        response: getStoresResponse,
    } = useAxios();

    const navigate = useNavigate();

    const onSubmit = () => {
        const values = form.getFieldsValue(true);
        values.stores_permission = isCheckAll
            ? stores.map((item: Store) => item.id)
            : isChecked;
        if (values.stores_permission.length === 0) {
            return message.error('Nenhuma loja selecionada');
        }
        const params = {
            method: MethodList.POST,
            url: '/subusers',
            data: {
                email: values.email,
                permission: values.role,
                stores: values.stores_permission,
            },
        };
        fetch(params);
    };

    const handleClick = (e: CheckboxChangeEvent) => {
        const { id, checked } = e.target;

        setIsChecked([...isChecked!, id!]);
        if (!checked) {
            setIsChecked(isChecked!.filter((item: any) => item !== id));
        }
    };

    const serializeStores = ({ data }: { data: any[] }) => {
        const storesArr = [] as any;
        data.forEach((store) => {
            const storeData = {
                storeId: store.id,
                storeName: store.name,
            };

            const checkStoresArr = storesArr.some((el: any) => {
                return el.storeId === storeData.storeId;
            });

            if (!checkStoresArr) {
                storesArr.push(storeData);
            }
        });
        return storesArr;
    };

    useEffect(() => {
        if (!getStoresResponse?.data) return;
        setLoadedStores(serializeStores(getStoresResponse.data));
    }, [getStoresResponse]);

    useEffect(() => {
        if (response?.config?.method === 'post') {
            message.success(`O usuário foi criado com sucesso`);
            form.resetFields();
            navigate('/shopmanagers/users/list');
        }
    }, [response]);

    useEffect(() => {
        if (error?.response) {
            const { errors } = error?.response?.data;
            if (errors) return errorsListing(errors);
            if (error?.response?.data.message) {
                message.error(error?.response?.data.message);
            }
        } else if (error) {
            message.error(
                'Um erro inesperado aconteceu, tente novamente mais tarde',
            );
        }
    }, [error]);

    const getStores = React.useCallback(async (token: CancelToken) => {
        try {
            const options = {
                method: MethodList.GET,
                url: 'store',
                cancelToken: token,
                params: {
                    perPage: 50,
                },
            };

            const response = await fetchGet(options);
            if (response?.data.data.length > 0) {
                setStores([...response?.data.data]);
            }
        } catch (error: unknown) {
            if (axios.isAxiosError(error) && error.response?.status !== 401) {
                message.error('Um erro inesperado ocorreu');
            }
        }
    }, []);

    useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        getStores(source.token);
        return () => {
            setStores([]);
            source.cancel('Request Cancelada');
        };
    }, [getStores]);

    useEffect(() => {
        setIsCheckAll(
            isChecked.length === loadedStores.length ||
                storeClientsSelected === ESelection.ALL_STORES,
        );
    }, [loadedStores, isChecked, storeClientsSelected]);

    return (
        <Row style={{ padding: '0 2rem' }}>
            <Col span={8} xs={24} sm={20} md={14} lg={12} xl={12} xxl={10}>
                <Form form={form} onFinish={onSubmit} layout="vertical">
                    <Form.Item
                        label="E-mail"
                        name="email"
                        rules={[
                            { required: true, max: 255 },
                            {
                                type: 'email',
                                message: 'Por favor informe um email válido',
                            },
                        ]}
                    >
                        <Input type="email" />
                    </Form.Item>
                    <Form.Item
                        label="Permissão"
                        name="role"
                        rules={[
                            {
                                required: true,
                                type: 'enum',
                                enum: listUsersRoles(),
                            },
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    label: 'Usuário Lojista',
                                    value: EnRoles.restocker,
                                },
                                {
                                    label: 'Usuário Síndico',
                                    value: EnRoles.trustee,
                                },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Permissão de acesso a lojas"
                        name="stores_permission"
                        rules={[
                            {
                                required: true,
                                message: 'Selecione as lojas',
                            },
                        ]}
                    >
                        <Radio.Group
                            onChange={(v) =>
                                setStoreClientsSelected(v.target.value)
                            }
                            value={storeClientsSelected}
                        >
                            <Space orientation="vertical">
                                <Radio value={ESelection.ALL_STORES}>
                                    Todas as lojas
                                </Radio>
                                <Radio value={ESelection.SOME_STORES}>
                                    Lojas específicas
                                </Radio>
                                {storeClientsSelected ===
                                    ESelection.SOME_STORES && (
                                    <Col
                                        style={{
                                            padding: '0 16px',
                                            height: 150,
                                            width: '100%',
                                            overflow: 'auto',
                                            border: '1px solid rgba(140, 140, 140, 0.35)',
                                        }}
                                    >
                                        {loadingGet ? (
                                            <Loading size="large" />
                                        ) : (
                                            <>
                                                <Form.Item>
                                                    <List
                                                        itemLayout="vertical"
                                                        dataSource={
                                                            loadedStores
                                                        }
                                                        renderItem={(
                                                            store: any,
                                                        ) => (
                                                            <>
                                                                <List.Item
                                                                    key={
                                                                        store.storeId
                                                                    }
                                                                >
                                                                    <Checkbox
                                                                        onChange={
                                                                            handleClick
                                                                        }
                                                                        checked={isChecked!.includes(
                                                                            store.storeId,
                                                                        )}
                                                                        id={
                                                                            store.storeId
                                                                        }
                                                                        disabled={
                                                                            !isEditMode
                                                                        }
                                                                    >
                                                                        {
                                                                            store.storeName
                                                                        }
                                                                    </Checkbox>
                                                                </List.Item>
                                                            </>
                                                        )}
                                                    />
                                                </Form.Item>
                                            </>
                                        )}
                                    </Col>
                                )}
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                    <Row justify="center" gutter={[0, 10]}>
                        <Col xs={24} sm={8} md={8} lg={8} xl={8} xxl={8}>
                            <Button
                                htmlType="reset"
                                icon={<ArrowLeftOutlined />}
                                onClick={() => navigate(-1)}
                                style={{ width: '100%' }}
                            >
                                Voltar
                            </Button>
                        </Col>
                        <Col
                            xs={{ span: 24, offset: 0 }}
                            sm={{ span: 8, offset: 1 }}
                            md={{ span: 8, offset: 1 }}
                            lg={{ span: 8, offset: 1 }}
                            xl={{ span: 8, offset: 1 }}
                            xxl={{ span: 8, offset: 1 }}
                        >
                            <Button
                                htmlType="submit"
                                className="default-btn"
                                loading={loading}
                                style={{
                                    width: breakpoints.sm ? undefined : '100%',
                                }}
                            >
                                {loading ? 'Salvando...' : 'Salvar'}
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
};

export default CreateAdmin;
